import { useEffect, useRef, useState } from 'react';
import PrayerAttemptRecovery from '@/components/PrayerAttemptRecovery';
import TestimonyDraftRecovery from '@/components/TestimonyDraftRecovery';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import AuthSection from '@/components/AuthSection';
import TextSizeToggle from '@/components/TextSizeToggle';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { BIBLE_ENABLED } from '@/lib/feature-flags';
import { getAppVersion, getVersionDetailsText } from '@/lib/app-version';
import { getReleaseCapabilities } from '@/lib/release-capabilities';
import { isCoreReadOnly } from '@/lib/core-cutover';
import styles from './SettingsPage.module.css';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const releaseCapabilities = getReleaseCapabilities();
  const appVersion = getAppVersion();
  const versionText = t('settings.version', {
    version: appVersion.version,
    sha: appVersion.shortSha,
  });
  const [versionCopied, setVersionCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);
  // App notice stack: the in-flow spacer below the Back link keeps the title
  // and the first sections (text size) below the fixed notices. Chrome only.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  // Copies build and browser facts for a bug report (no account or prayer
  // data; see app-version.ts). A failed or unavailable clipboard stays silent.
  const handleCopyVersion = async () => {
    try {
      await navigator.clipboard.writeText(getVersionDetailsText(i18n.language ?? 'en'));
    } catch {
      return;
    }
    setVersionCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setVersionCopied(false), 1500);
  };

  return (
    <div className={styles.container}>
      <Link to="/" className={styles.backLink}>{t('settings.back')}</Link>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <h1 className={styles.title}>{t('settings.title')}</h1>

      {/* Core-content cutover: a plain statement of the read-only state
          for anyone who comes here looking for an explanation. Reuses the
          existing section chrome; no new CSS. */}
      {isCoreReadOnly() && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('settings.readOnlyTitle')}</h2>
          <p className={styles.description}>{t('maintenance.settingsNote')}</p>
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.textSizeTitle')}</h2>
        <TextSizeToggle />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.language')}</h2>
        <LanguageSwitcher />
      </section>

      {BIBLE_ENABLED && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('bible.settingsTitle')}</h2>
          <Link to="/bible" className={styles.navRow}>
            <span className={styles.navRowLabel}>{t('bible.settingsRowLabel')}</span>
            <svg className={styles.navRowChevron} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Link>
        </section>
      )}

      <TestimonyDraftRecovery />
      <section className={styles.section}>
        <h2 id="settings-account-title" className={styles.sectionTitle}>{t('auth.accountTitle')}</h2>
        <AuthSection />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.aboutTitle')}</h2>
        <p className={styles.description}>{t('settings.aboutText')}</p>
        <div className={styles.versionRow}>
          <button
            type="button"
            className={styles.versionButton}
            onClick={handleCopyVersion}
            aria-label={`${versionText}. ${t('settings.versionCopyAria')}`}
          >
            {versionText}
          </button>
          <span className={styles.versionCopied} role="status" aria-live="polite">
            {versionCopied ? t('settings.versionCopied') : ''}
          </span>
        </div>
        {/* PC-B rev 9 decision 5: display the SAME in-memory release
            capability tuple the bundled JS installed on window — the
            operator compares all three fields to the release manifest in
            every context during the R0-R6 cutover. Not user-facing prose;
            deliberately raw values. */}
        <p className={styles.version}>
          {releaseCapabilities.buildId}
          {' · '}
          {releaseCapabilities.containment}
          {' · envelope '}
          {releaseCapabilities.keepsakeEnvelope}
        </p>
      </section>

      {!isCoreReadOnly() && <PrayerAttemptRecovery />}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.dataTitle')}</h2>
        <p className={styles.description}>{t('settings.dataText')}</p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('settings.offlineTitle')}</h2>
        <p className={styles.description}>{t('settings.offlineText')}</p>
      </section>
    </div>
  );
}
